/**
 * Admin → Adapters
 *
 * Phase 5b item 9 (2026-05-16). Symmetric-adapter architecture per
 * CLAUDE.md Architecture Rule 13 - the four adapter axes are first-class:
 *
 *   • cmp  - alternative CMP that Discovery routes consent into
 *   • dspm - alternative DSPM that FOCTTA core routes data-discovery into
 *   • dam  - alternative DAM
 *   • ddr  - alternative DDR
 *
 * Each axis can have zero or more vendor adapters; this page tabs across
 * the four axes and lists vendor / status / cache-ttl / fail-open posture
 * / direction (pull/push). Read-only for now; vendor onboarding is a
 * separate wizard (next sub-deliverable).
 *
 * Backend:
 *   GET /v1/foundational/adapters/:axis → { adapters: [...] }
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  Plug,
  CheckCircle2,
  PauseCircle,
  XCircle,
  ArrowRightLeft,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import { listAdapters, type Adapter, type AdapterAxis, type AdapterStatus } from '@/lib/api';

const AXES: ReadonlyArray<{ value: AdapterAxis; label: string; helper: string }> = [
  { value: 'cmp', label: 'CMP', helper: 'consent platform peers' },
  { value: 'dspm', label: 'DSPM', helper: 'data-security posture peers' },
  { value: 'dam', label: 'DAM', helper: 'database-activity peers' },
  { value: 'ddr', label: 'DDR', helper: 'detection-response peers' },
];

const STATUS_VARIANT: Record<AdapterStatus, 'success' | 'warning' | 'critical'> = {
  active: 'success',
  paused: 'warning',
  error: 'critical',
};

const STATUS_ICON: Record<AdapterStatus, typeof CheckCircle2> = {
  active: CheckCircle2,
  paused: PauseCircle,
  error: XCircle,
};

function directionSummary(dir: Record<string, boolean>): string {
  const on = Object.entries(dir).filter(([, v]) => v).map(([k]) => k);
  return on.length === 0 ? '-' : on.join(' · ');
}

export function AdminAdapters(): JSX.Element {
  const [axis, setAxis] = useState<AdapterAxis>('cmp');
  const [adaptersByAxis, setAdaptersByAxis] = useState<Record<AdapterAxis, Adapter[]>>({
    cmp: [], dspm: [], dam: [], ddr: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled(AXES.map((a) => listAdapters(a.value)));
      const next: Record<AdapterAxis, Adapter[]> = { cmp: [], dspm: [], dam: [], ddr: [] };
      const errs: string[] = [];
      results.forEach((r, i) => {
        const ax = AXES[i]!.value;
        if (r.status === 'fulfilled') next[ax] = r.value.adapters;
        else errs.push(`${ax}: ${(r.reason as Error)?.message ?? 'failed'}`);
      });
      setAdaptersByAxis(next);
      setError(errs.length > 0 ? errs.join(' · ') : null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const current = adaptersByAxis[axis];
  const totals = useMemo(() => {
    const total = AXES.reduce((acc, a) => acc + adaptersByAxis[a.value].length, 0);
    const active = AXES.reduce(
      (acc, a) => acc + adaptersByAxis[a.value].filter((x) => x.status === 'active').length,
      0,
    );
    return { total, active };
  }, [adaptersByAxis]);

  return (
    <PageContainer>
      <PageHeader
        title="Adapters"
        subtitle="Symmetric-adapter architecture - interop with alternative CMP / DSPM / DAM / DDR vendors. Each axis is independent and works best with FOCTTA but never requires it."
        badges={[
          { label: 'Symmetric · 4 axes', variant: 'glass' },
          { label: '100-pin doctrine', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Some adapter axes unavailable"
          detail={error}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Adapters configured" value={totals.total} icon={Plug} hint="across 4 axes" />
        <KpiCard label="Active" value={totals.active} icon={CheckCircle2} glow={totals.active > 0 ? 'high' : 'low'} hint="ready to route" />
        <KpiCard label="On this axis" value={current.length} icon={Plug} hint={AXES.find((a) => a.value === axis)?.helper ?? ''} />
        <KpiCard label="Active on axis" value={current.filter((c) => c.status === 'active').length} icon={ArrowRightLeft} hint="routing live" />
      </div>

      {/* Axis tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {AXES.map((a) => (
          <button
            key={a.value}
            type="button"
            onClick={() => setAxis(a.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              axis === a.value
                ? 'bg-primary text-primary-foreground'
                : 'glass-lite hover:bg-muted/30'
            }`}
            title={a.helper}
          >
            {a.label}
            <span className="ml-1.5 text-[10px] opacity-75 tabular">
              ({adaptersByAxis[a.value].length})
            </span>
          </button>
        ))}
      </div>

      <GlassCard className="p-6">
        <CardHeader className="p-0 mb-3">
          <CardTitle>{AXES.find((a) => a.value === axis)?.label} adapters</CardTitle>
          <CardDescription>
            {AXES.find((a) => a.value === axis)?.helper}. <code className="font-mono text-xs">failOpenOnUnavailable</code>
            {' '}controls whether Discovery routes traffic anyway when this vendor is down.
          </CardDescription>
        </CardHeader>

        {loading && <LoadingState caption="Loading adapters…" />}

        {!loading && current.length === 0 && (
          <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
            <Plug className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <div className="font-medium text-foreground/90">No {axis.toUpperCase()} adapters configured</div>
            <div className="mt-1">
              Add vendors via the onboarding wizard (next sub-deliverable). FOCTTA core remains the
              default peer regardless.
            </div>
          </div>
        )}

        {!loading && current.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs tabular">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border/30">
                  <th className="py-2 pr-3 font-normal">Vendor</th>
                  <th className="py-2 pr-3 font-normal">Status</th>
                  <th className="py-2 pr-3 font-normal">Pull from</th>
                  <th className="py-2 pr-3 font-normal">Push to</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">Cache TTL</th>
                  <th className="py-2 pr-3 font-normal">Fail-open?</th>
                  <th className="py-2 pr-3 font-normal whitespace-nowrap">Last sync</th>
                </tr>
              </thead>
              <tbody>
                {current.map((a, i) => {
                  const StatusIcon = STATUS_ICON[a.status];
                  return (
                    <motion.tr
                      key={a.id}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(i * 0.01, 0.3) }}
                      className="border-b border-border/15 hover:bg-muted/20 align-top"
                    >
                      <td className="py-2 pr-3 font-mono text-[11px]">{a.vendor}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <Badge variant={STATUS_VARIANT[a.status]} className="text-[10px] inline-flex items-center gap-1">
                          <StatusIcon className="h-3 w-3" />
                          {a.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 max-w-[20ch] truncate" title={directionSummary(a.pullDirection)}>
                        {directionSummary(a.pullDirection)}
                      </td>
                      <td className="py-2 pr-3 max-w-[20ch] truncate" title={directionSummary(a.pushDirection)}>
                        {directionSummary(a.pushDirection)}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{a.cacheTtlSeconds}s</td>
                      <td className="py-2 pr-3">
                        <Badge variant={a.failOpenOnUnavailable ? 'warning' : 'glass'} className="text-[10px]">
                          {a.failOpenOnUnavailable ? 'yes' : 'no'}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                        {a.lastSyncAt ? new Date(a.lastSyncAt).toLocaleString() : '-'}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </PageContainer>
  );
}
